import { useTranslation } from 'react-i18next'
import { Mail, Phone, MapPin } from 'lucide-react'
import { usePageSEO } from '@/hooks/usePageSEO'
import {
  LegalPageLayout,
  LegalSection,
  LegalList,
  type LegalSectionItem,
} from '@/components/legal/LegalPageLayout'

const SECTION_KEYS = [
  'controller',
  'dataCollected',
  'purposes',
  'retention',
  'rights',
  'recipients',
  'security',
  'contact',
] as const

type PrivacySectionKey = (typeof SECTION_KEYS)[number]

// Sections that have an `items` array of bullet points.
const SECTIONS_WITH_ITEMS: readonly PrivacySectionKey[] = [
  'dataCollected',
  'purposes',
  'retention',
  'rights',
]

export default function PrivacyPage() {
  const { t } = useTranslation()

  usePageSEO({
    title: t('meta.privacy.title'),
    description: t('meta.privacy.description'),
    ogTitle: t('meta.privacy.ogTitle'),
    ogDescription: t('meta.privacy.ogDescription'),
  })

  const toc: LegalSectionItem[] = SECTION_KEYS.map((key) => ({
    id: `privacy-${key}`,
    number: t(`legal.privacy.sections.${key}.number`),
    title: t(`legal.privacy.sections.${key}.title`),
  }))

  return (
    <LegalPageLayout
      documentNumber="01 · PRIV"
      eyebrow={t('legal.privacy.eyebrow')}
      title={t('privacy.title')}
      subtitle={t('legal.privacy.headerSubtitle')}
      intro={t('legal.privacy.intro')}
      toc={toc}
    >
      {SECTION_KEYS.map((key) => {
        const id = `privacy-${key}`
        const number = t(`legal.privacy.sections.${key}.number`)
        const title = t(`legal.privacy.sections.${key}.title`)
        const body = t(`legal.privacy.sections.${key}.body`)
        const hasItems = SECTIONS_WITH_ITEMS.includes(key)
        const items = hasItems
          ? (t(`legal.privacy.sections.${key}.items`, {
              returnObjects: true,
            }) as string[])
          : null

        return (
          <LegalSection
            key={key}
            id={id}
            number={number}
            title={title}
            body={body}
          >
            {items && Array.isArray(items) && <LegalList items={items} />}
            {key === 'contact' && <ContactBlock />}
          </LegalSection>
        )
      })}
    </LegalPageLayout>
  )
}

function ContactBlock() {
  const { t } = useTranslation()
  const email = t('contact.email')
  const phone = t('contact.phone')
  const address = t('contact.address')
  const city = t('contact.city')

  return (
    <dl className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink-200 border border-ink-200 max-w-3xl">
      <ContactField
        icon={<Mail className="h-4 w-4" aria-hidden="true" strokeWidth={2.25} />}
        label={t('legal.privacy.sections.contact.contactEmailLabel')}
      >
        <a
          href={`mailto:${email}`}
          className="hover:text-brand-600 transition-colors break-all"
        >
          {email}
        </a>
      </ContactField>
      <ContactField
        icon={<Phone className="h-4 w-4" aria-hidden="true" strokeWidth={2.25} />}
        label={t('legal.privacy.sections.contact.contactPhoneLabel')}
      >
        <a
          href={`tel:${phone.replace(/\s+/g, '')}`}
          className="hover:text-brand-600 transition-colors"
        >
          {phone}
        </a>
      </ContactField>
      <ContactField
        icon={
          <MapPin className="h-4 w-4" aria-hidden="true" strokeWidth={2.25} />
        }
        label={t('legal.privacy.sections.contact.contactAddressLabel')}
      >
        <span>{address}</span>
        <br />
        <span>{city}</span>
      </ContactField>
    </dl>
  )
}

function ContactField({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="relative bg-white p-5">
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 h-[2px] w-8 bg-brand-500"
      />
      <dt className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500">
        <span className="text-brand-500">{icon}</span>
        {label}
      </dt>
      <dd className="mt-2 text-sm font-semibold text-ink-900 leading-relaxed">
        {children}
      </dd>
    </div>
  )
}
